import React, { useRef, useEffect, useCallback, memo, useImperativeHandle, forwardRef } from 'react';

/**
 * CardContextMenu Component (Kanban)
 * 
 * SIMPLE drag handle for Kanban cards.
 * 
 * This component ONLY renders the drag handle visual element.
 * It dispatches a custom event ('openKanbanCardMenu') when clicked,
 * which KanbanCard listens for and opens the shared DragHandleMenu from Flow view.
 * 
 * This ensures there's only ONE menu component to maintain across all views.
 * 
 * Click to show menu (via event dispatch), drag to move card.
 * Also supports right-click context menu via exposed openAtPosition method.
 */
const CardContextMenu = forwardRef(({
  task,
  dragListeners,
}, ref) => {
  const handleRef = useRef(null);
  
  // Track pointer for click vs drag detection
  const pointerStartRef = useRef(null);
  const isTrackingRef = useRef(false);

  // Expose method to open menu at a specific position (for right-click from parent)
  useImperativeHandle(ref, () => ({
    openAtPosition: (x, y) => {
      // Dispatch event for KanbanCard to open its DragHandleMenu
      window.dispatchEvent(new CustomEvent('openKanbanCardMenu', {
        detail: { 
          taskId: task.id,
          position: { x, y }
        }
      }));
    },
    closeMenu: () => {
      // The menu is now managed by KanbanCard, so we just dispatch a close event
      window.dispatchEvent(new CustomEvent('closeKanbanCardMenu', {
        detail: { taskId: task.id }
      }));
    },
  }), [task.id]);

  // Handle document-level pointer up to detect click vs drag
  const handleDocumentPointerUp = useCallback((e) => {
    if (!pointerStartRef.current || !isTrackingRef.current) return;
    
    // Only respond to left-button releases
    if (e.button !== 0) {
      pointerStartRef.current = null;
      isTrackingRef.current = false;
      return;
    }
    
    const deltaX = Math.abs(e.clientX - pointerStartRef.current.x);
    const deltaY = Math.abs(e.clientY - pointerStartRef.current.y);
    const deltaTime = Date.now() - pointerStartRef.current.time;
    
    // If it was a quick click without much movement, dispatch event to open menu
    if (deltaX < 5 && deltaY < 5 && deltaTime < 300) {
      if (handleRef.current) {
        const rect = handleRef.current.getBoundingClientRect();
        
        // Dispatch custom event for KanbanCard to open its DragHandleMenu
        window.dispatchEvent(new CustomEvent('openKanbanCardMenu', {
          detail: { 
            taskId: task.id,
            // Pass the handle's bounding rect so the menu can position itself
            handleRect: {
              top: rect.top,
              left: rect.left,
              right: rect.right,
              bottom: rect.bottom,
            }
          }
        }));
      }
    }
    
    pointerStartRef.current = null;
    isTrackingRef.current = false;
  }, [task.id]);

  // Set up document listener for pointer up
  useEffect(() => {
    document.addEventListener('pointerup', handleDocumentPointerUp);
    return () => {
      document.removeEventListener('pointerup', handleDocumentPointerUp);
    };
  }, [handleDocumentPointerUp]);

  // Handle pointer down - start tracking for click vs drag
  const handlePointerDown = useCallback((e) => {
    // Only track left clicks
    if (e.button !== 0) return;
    
    pointerStartRef.current = { 
      x: e.clientX, 
      y: e.clientY, 
      time: Date.now()
    };
    isTrackingRef.current = true;
    
    // Call dnd-kit's handler to enable drag
    dragListeners?.onPointerDown?.(e);
  }, [dragListeners]);

  return (
    <div 
      ref={handleRef}
      className="kanban-card__drag-handle"
      title="Click for options, drag to move"
      onPointerDown={handlePointerDown}
      style={{ touchAction: 'none' }}
    >
      <svg width="10" height="14" viewBox="0 0 10 14" fill="currentColor">
        <circle cx="2" cy="2" r="1.5" />
        <circle cx="8" cy="2" r="1.5" />
        <circle cx="2" cy="7" r="1.5" />
        <circle cx="8" cy="7" r="1.5" />
        <circle cx="2" cy="12" r="1.5" />
        <circle cx="8" cy="12" r="1.5" />
      </svg>
    </div>
  );
});

CardContextMenu.displayName = 'CardContextMenu';

export default memo(CardContextMenu);
